/**
 * CaseName - Displays case/lead name with inline case-change, conflict check,
 * and action buttons (download, invite, mark-unread, sidebar toggle).
 *
 * All business logic lives in useCaseName.
 */

import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Link } from 'react-router-dom';

import CaseListDropDown from 'components/CaseListDropDown/CaseListDropDown';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { generateFullName } from 'utils/generateFullNameUtils';
import { useMessageContext } from '../../context';
import { useCaseName } from '../../hooks/useCaseName';

// ── Sub-components ────────────────────────────────────────────────────────────

const ConflictUserItem = memo(({ user }) => {
  const label = generateFullName(user) || user?.group_name || 'Unknown';
  const role = user?.access_level ? ` (${user.access_level})` : user?.res_type === 'user-group' ? ' (Group)' : '';
  return (
    <li>
      {label}
      {role}
    </li>
  );
});

// ── Main component ────────────────────────────────────────────────────────────

function CaseName({ messageDetails, setShowSideBar, showSideBar, handleMarkAsUnRead, hasPermission, setShowInviteDialog }) {
  const { checkSubPermission } = useUserDetailsContext();
  const { isGlobal } = useMessageContext();

  const {
    newMsg,
    showCaseSearch,
    selectedCase,
    caseError,
    isLoadingCaseUsers,
    conflictUsers,
    showConflictDialog,
    isMutating,
    handleEditCase,
    handleCaseDropdownChange,
    resetCaseSearch,
    handleConfirmCaseChange,
    doChangeCase,
    handleConflictDialogHide,
    handleInviteClick,
    handleSidebarToggle,
    downloadSecureMessagePDF,
  } = useCaseName({ messageDetails, setShowInviteDialog, setShowSideBar });

  const conflictDialogFooter = (
    <div className="d-flex justify-content-end gap-2">
      <Button label="Cancel" className="p-button-secondary outline" onClick={handleConflictDialogHide} />
      <Button
        label="Yes, Change Case"
        icon="pi pi-check"
        className="p-button-secondary"
        onClick={doChangeCase}
        disabled={isMutating}
        loading={isMutating}
      />
    </div>
  );

  return (
    <>
      <div
        className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2"
        role="region"
        aria-label="Case information and actions"
      >
        {/* ── Left: case label + inline edit ─────────────────────────────── */}
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="F-size14 text-bold">{messageDetails?.is_lead ? 'Lead' : 'Case'}: </span>

          {!showCaseSearch ? (
            <>
              <Link
                to={`/${messageDetails?.is_lead ? 'leads' : 'cases'}/${messageDetails?.case?.case_id || ''}`}
                className="F-size14 text-primary-dark text-decoration-none case-details-link pointer"
                aria-label={`View ${messageDetails?.is_lead ? 'lead' : 'case'} details for ${
                  messageDetails?.case?.case_name || 'Untitled'
                }`}
              >
                {messageDetails?.case?.case_name || 'Untitled'}
              </Link>

              {isGlobal && (
                <Button
                  icon="icon-edit"
                  className="p-button-rounded p-button-text"
                  onClick={handleEditCase}
                  aria-label="Edit case"
                  tooltip="Edit Case"
                  tooltipOptions={{ position: 'top', showDelay: 500 }}
                />
              )}
            </>
          ) : (
            <div className="d-flex flex-column">
              <div className="d-flex align-items-center gap-2">
                <CaseListDropDown
                  value={selectedCase}
                  caseOnly
                  onChange={handleCaseDropdownChange}
                  name="case"
                  className="new-task-popup invite-user input-shadow"
                  placeholder="Search Case"
                  style={{ minWidth: '220px' }}
                />
                <Button
                  icon="pi pi-times"
                  className="p-button-secondary p-button-sm outline"
                  onClick={resetCaseSearch}
                  aria-label="Cancel case change"
                  tooltip="Cancel"
                  tooltipOptions={{ position: 'top' }}
                />
                <Button
                  icon={isLoadingCaseUsers ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                  className="p-button-secondary p-button-sm"
                  onClick={handleConfirmCaseChange}
                  disabled={isLoadingCaseUsers}
                  aria-label="Confirm case change"
                  tooltip="Confirm"
                  tooltipOptions={{ position: 'top' }}
                />
              </div>
              {caseError && <span className="text-danger F-size12 mt-1">{caseError}</span>}
            </div>
          )}
        </div>

        {/* ── Right: action buttons ───────────────────────────────────────── */}
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Button
            label="Download"
            icon="pi pi-download"
            className="p-button-secondary p-button-sm outline"
            onClick={downloadSecureMessagePDF}
            aria-label="Download message thread as PDF"
          />

          {hasPermission && (
            <Button
              label="Invite"
              icon="pi pi-user-plus"
              className="p-button-secondary p-button-sm outline"
              onClick={handleInviteClick}
              aria-label="Invite users to this conversation"
            />
          )}

          <Button
            label="Mark as Unread"
            className="p-button-secondary p-button-sm outline"
            onClick={(e) => handleMarkAsUnRead(e, messageDetails, true)}
            aria-label="Mark this thread as unread"
          />

          {checkSubPermission('secure_message_sidebar') && (
            <Button
              label={showSideBar ? 'Hide Sidebar' : 'Show Sidebar'}
              icon={showSideBar ? 'pi pi-times' : 'pi pi-comments'}
              className="p-button-secondary p-button-sm outline"
              onClick={handleSidebarToggle}
              aria-label={showSideBar ? 'Hide sidebar chat' : 'Show sidebar chat'}
              aria-expanded={showSideBar}
              style={{ overflow: 'inherit' }}
            >
              {!showSideBar && newMsg && <span className="indicator-dot sidebar-unread-badge--pulse" />}
            </Button>
          )}
        </div>
      </div>

      {/* ── Case-change conflict dialog ─────────────────────────────────────── */}
      <Dialog
        header="User Conflict"
        visible={showConflictDialog}
        onHide={handleConflictDialogHide}
        style={{ width: '40vw', minWidth: '360px' }}
        className="common-popup-style"
        footer={conflictDialogFooter}
        modal
      >
        <p className="mb-2">
          The following users are not linked to <strong>{selectedCase?.case_name || 'the selected case'}</strong>:
        </p>
        <ul className="mb-3">
          {conflictUsers.map((user, i) => (
            <ConflictUserItem key={user?.client_cognito_username || user?.contact_id || user?.user_group_id || i} user={user} />
          ))}
        </ul>
        <p className="mb-0">Do you still want to change the case for this message?</p>
      </Dialog>
    </>
  );
}

CaseName.propTypes = {
  messageDetails: PropTypes.shape({
    case_id: PropTypes.string,
    is_lead: PropTypes.bool,
    pk: PropTypes.string,
    message_thread_id: PropTypes.string,
    case_name: PropTypes.string,
    firm_users: PropTypes.arrayOf(PropTypes.object),
    contacts: PropTypes.arrayOf(PropTypes.object),
    groups: PropTypes.arrayOf(PropTypes.object),
    has_new_sidebar_messages: PropTypes.bool,
    case: PropTypes.shape({
      case_id: PropTypes.string,
      case_name: PropTypes.string,
    }),
  }),
  setShowSideBar: PropTypes.func.isRequired,
  showSideBar: PropTypes.bool.isRequired,
  handleMarkAsUnRead: PropTypes.func.isRequired,
  hasPermission: PropTypes.bool.isRequired,
  setShowInviteDialog: PropTypes.func.isRequired,
  loadCaseContacts: PropTypes.func.isRequired,
  refetchDetails: PropTypes.func,
};

CaseName.defaultProps = {
  messageDetails: null,
  refetchDetails: undefined,
};

export default memo(CaseName);
